// File: app/src/components/workout/TimerSetupSheet.js
//
// The sheet where you set a timer up before loading it into the Coach (#109):
// a bottom sheet on phones (Start in thumb reach), a dialog on desktop.
// DraggableStepper controls, not number inputs, so no keyboard overlap.

import React, {
  useMemo,
  useState,
  useEffect,
  useCallback,
  useRef,
} from "react";
import DetailSheet from "../session/generate/DetailSheet";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { ChipGrid, DeleteConfirm } from "../profile/ProfileParts";
import DraggableStepper from "../ui/draggable-stepper";
import { TIMER_PRESETS } from "../../config/timers";
import { buildStandaloneSessionPlan } from "../../utils/devicePlan";
import { calculateTotalDuration } from "../../utils/time";
import { getWorkoutSettings } from "../../hooks/useWorkoutSettings";
import { Copy, Play, RotateCcw } from "lucide-react";

// Describes which config keys each mode exposes and how to label them.
// Numeric fields include max / step / unit for the DraggableStepper.

const MODE_FIELDS = {
  tabata: {
    fields: [
      { key: "work", label: "Work", min: 5, max: 300, step: 5, unit: "s" },
      { key: "rest", label: "Rest", min: 5, max: 300, step: 5, unit: "s" },
      { key: "rounds", label: "Rounds", min: 1, max: 50, step: 1 },
    ],
    setsKey: true,
  },
  hiit: {
    fields: [
      { key: "work", label: "Work", min: 5, max: 300, step: 5, unit: "s" },
      { key: "rest", label: "Rest", min: 5, max: 300, step: 5, unit: "s" },
      { key: "rounds", label: "Rounds", min: 1, max: 50, step: 1 },
    ],
    setsKey: true,
  },
  emom: {
    fields: [
      {
        key: "duration",
        label: "Every",
        type: "select",
        options: [
          { value: 60, label: "1 min" },
          { value: 120, label: "2 min" },
          { value: 180, label: "3 min" },
          { value: 240, label: "4 min" },
          { value: 300, label: "5 min" },
        ],
      },
      { key: "rounds", label: "Intervals", min: 1, max: 60, step: 1 },
    ],
    setsKey: true,
  },
  fgb: {
    fields: [
      { key: "exercises", label: "Stations", min: 1, max: 20, step: 1 },
      {
        key: "exerciseTime",
        label: "Station time",
        min: 10,
        max: 300,
        step: 5,
        unit: "s",
      },
      { key: "rounds", label: "Rounds", min: 1, max: 20, step: 1 },
      {
        key: "setRest",
        label: "Rest between rounds",
        min: 0,
        max: 300,
        step: 5,
        unit: "s",
      },
    ],
    setsKey: false,
  },
  amrap: {
    fields: [
      {
        key: "duration",
        label: "Duration",
        min: 60,
        max: 3600,
        step: 30,
        unit: "s",
      },
    ],
    setsKey: true,
  },
  countdown: {
    fields: [
      {
        key: "duration",
        label: "Duration",
        min: 10,
        max: 3600,
        step: 10,
        unit: "s",
      },
    ],
    setsKey: true,
  },
  circuit: {
    fields: [
      { key: "exercises", label: "Exercises", min: 1, max: 20, step: 1 },
      {
        key: "exerciseTime",
        label: "Exercise",
        min: 5,
        max: 300,
        step: 5,
        unit: "s",
      },
      { key: "restTime", label: "Rest", min: 0, max: 300, step: 5, unit: "s" },
      { key: "rounds", label: "Rounds", min: 1, max: 20, step: 1 },
    ],
    setsKey: false,
  },
};

const JUST_SAVED_BADGE_TIMEOUT_MS = 2000;

export const PRESET_HAS_CONFIG = (mode) => Boolean(MODE_FIELDS[mode]);

const formatDuration = (seconds) => {
  if (!seconds || seconds <= 0) return "—";
  // Round first: rep-based estimates make the compiled total fractional. (#39)
  const total = Math.round(seconds);
  const m = Math.floor(total / 60);
  const s = total % 60;
  return `${m}:${String(s).padStart(2, "0")}`;
};

const settingsEqual = (a, b) => {
  if (!a || !b) return false;
  const keys = new Set([...Object.keys(a), ...Object.keys(b)]);
  for (const key of keys) {
    if ((a[key] ?? undefined) !== (b[key] ?? undefined)) return false;
  }
  return true;
};

const TimerSetupSheet = ({
  open,
  mode,
  onClose,
  onConfirm,
  // Saved-timer props
  presetId,
  presetName,
  initialSettings,
  onSave,
  onDuplicate,
  onDelete,
}) => {
  const spec = mode ? MODE_FIELDS[mode] : null;
  const preset = mode ? TIMER_PRESETS[mode] : null;

  const baseSettings = useMemo(
    () => initialSettings || preset?.settings || {},
    [initialSettings, preset],
  );

  const [config, setConfig] = useState(() => ({ ...baseSettings }));
  const [showSaveInput, setShowSaveInput] = useState(false);
  const [saveName, setSaveName] = useState("");
  const [notice, setNotice] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const saveInputRef = useRef(null);
  const noticeTimerRef = useRef(null);

  const clearNoticeTimer = useCallback(() => {
    if (noticeTimerRef.current !== null) {
      window.clearTimeout(noticeTimerRef.current);
      noticeTimerRef.current = null;
    }
  }, []);

  const flash = useCallback(
    (text) => {
      clearNoticeTimer();
      setNotice(text);
      noticeTimerRef.current = window.setTimeout(() => {
        setNotice(null);
        noticeTimerRef.current = null;
      }, JUST_SAVED_BADGE_TIMEOUT_MS);
    },
    [clearNoticeTimer],
  );

  useEffect(() => {
    const settings = initialSettings || preset?.settings || {};
    if (open) {
      setConfig({ ...settings });
    }
    clearNoticeTimer();
    setShowSaveInput(false);
    setSaveName("");
    setNotice(null);
    setConfirmDelete(false);
  }, [open, mode, preset, initialSettings, clearNoticeTimer]);

  useEffect(() => () => clearNoticeTimer(), [clearNoticeTimer]);

  useEffect(() => {
    if (!open || !showSaveInput) return;
    saveInputRef.current?.focus();
    saveInputRef.current?.select?.();
  }, [open, showSaveInput]);

  const totalSeconds = useMemo(() => {
    if (!mode) return 0;
    try {
      const plan = buildStandaloneSessionPlan(mode, config);
      const { skipLastRest } = getWorkoutSettings();
      return calculateTotalDuration(plan, { skipLastRest });
    } catch {
      return 0;
    }
  }, [mode, config]);

  const dirty = useMemo(
    () => !settingsEqual(config, baseSettings),
    [config, baseSettings],
  );

  const isCustom = Boolean(presetId);

  const handleReset = useCallback(() => {
    setConfig({ ...(preset?.settings || {}) });
  }, [preset]);

  const handleSaveAs = useCallback(() => {
    setSaveName(presetName ? `${presetName} (copy)` : `My ${preset?.label || mode}`);
    setShowSaveInput(true);
  }, [presetName, preset, mode]);

  const handleConfirmSaveAs = useCallback(() => {
    const name = saveName.trim();
    if (!onSave || !name) return;
    onSave({ id: undefined, name, mode, settings: { ...config } });
    setShowSaveInput(false);
    flash(`Saved as ${name}`);
  }, [config, flash, mode, onSave, saveName]);

  const handleSaveInPlace = useCallback(() => {
    if (!onSave || !presetId) return;
    onSave({ id: presetId, name: presetName, mode, settings: { ...config } });
    flash("Changes saved");
  }, [config, flash, mode, onSave, presetId, presetName]);

  if (!mode || !spec || !preset) return null;

  const setField = (key, raw) => {
    const parsed = Number(raw);
    setConfig((prev) => ({
      ...prev,
      [key]: Number.isFinite(parsed) ? parsed : prev[key],
    }));
  };

  const sets = config.sets ?? 1;
  const showSetRest = spec.setsKey && sets > 1;

  const footer = (
    <div className="grid gap-2">
      <Button
        type="button"
        className="h-12 w-full gap-2 text-base font-semibold"
        onClick={() => onConfirm?.({ ...config })}
      >
        <Play className="h-4 w-4 fill-current" />
        Start
      </Button>
      {onSave && !showSaveInput && (
        <div className="flex flex-wrap justify-center gap-x-5 gap-y-1 text-sm font-semibold text-primary">
          {isCustom && dirty && (
            <button type="button" onClick={handleSaveInPlace} className="py-1">
              Save changes
            </button>
          )}
          <button type="button" onClick={handleSaveAs} className="py-1">
            {isCustom ? "Save as new" : "Save as my timer"}
          </button>
        </div>
      )}
    </div>
  );

  return (
    <DetailSheet
      open={open}
      onOpenChange={(next) => {
        if (!next) onClose?.();
      }}
      title={isCustom ? presetName : preset.label}
      description={isCustom ? `${preset.label} · ${preset.description}` : preset.description}
      showDescription
      footer={footer}
    >
      {spec.fields.map((field) =>
        field.type === "select" ? (
          <div key={field.key} className="grid gap-2">
            <span className="text-center text-[10px] font-semibold uppercase tracking-widest text-muted-foreground">
              {field.label}
            </span>
            <ChipGrid
              columns={3}
              options={field.options}
              value={Number(config[field.key] ?? field.options[0].value)}
              onChange={(v) => setField(field.key, v)}
            />
          </div>
        ) : (
          <DraggableStepper
            key={field.key}
            label={field.label}
            value={config[field.key] ?? field.min ?? 0}
            onChange={(val) => setConfig((prev) => ({ ...prev, [field.key]: val }))}
            min={field.min ?? 0}
            max={field.max ?? 999}
            step={field.step ?? 1}
            unit={field.unit}
          />
        ),
      )}

      {spec.setsKey && (
        <DraggableStepper
          label="Sets"
          value={config.sets ?? 1}
          onChange={(val) => setConfig((prev) => ({ ...prev, sets: val }))}
          min={1}
          max={20}
          step={1}
        />
      )}

      {showSetRest && (
        <DraggableStepper
          label="Rest between sets"
          value={config.setRest ?? 0}
          onChange={(val) => setConfig((prev) => ({ ...prev, setRest: val }))}
          min={0}
          max={300}
          step={5}
          unit="s"
        />
      )}

      <div className="flex items-center justify-between rounded-xl border border-border bg-background px-3 py-2.5 text-sm">
        <span className="text-muted-foreground">Total</span>
        <span className="flex items-center gap-3">
          {dirty && (
            <button
              type="button"
              onClick={handleReset}
              className="flex items-center gap-1 text-xs text-muted-foreground transition-colors hover:text-foreground"
            >
              <RotateCcw className="h-3 w-3" />
              Reset
            </button>
          )}
          <span className="font-mono font-semibold tabular-nums">{formatDuration(totalSeconds)}</span>
        </span>
      </div>

      {notice && <p className="text-center text-xs text-green-500">{notice}</p>}

      {showSaveInput && (
        <div className="grid gap-2 rounded-xl border border-border bg-background p-3">
          <label htmlFor="timer-save-name" className="mb-0 text-sm font-semibold text-foreground">
            Name
          </label>
          <div className="flex items-center gap-2">
            <Input
              id="timer-save-name"
              ref={saveInputRef}
              value={saveName}
              onChange={(e) => setSaveName(e.target.value)}
              className="flex-1"
              onKeyDown={(e) => {
                if (e.key === "Enter" && saveName.trim()) {
                  e.preventDefault();
                  handleConfirmSaveAs();
                }
                if (e.key === "Escape") {
                  e.preventDefault();
                  e.stopPropagation();
                  setShowSaveInput(false);
                }
              }}
            />
            <Button size="sm" disabled={!saveName.trim()} onClick={handleConfirmSaveAs}>
              Save
            </Button>
          </div>
        </div>
      )}

      {isCustom && (onDuplicate || onDelete) && (
        <div className="grid gap-3">
          {onDuplicate && !confirmDelete && (
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="justify-self-start"
              onClick={() => {
                onDuplicate();
                flash("Copy saved to your timers");
              }}
            >
              <Copy className="mr-1.5 h-3.5 w-3.5" />
              Duplicate
            </Button>
          )}
          {onDelete && (
            <DeleteConfirm
              name={presetName}
              consequence="It goes from your timers on every device."
              confirming={confirmDelete}
              onAsk={() => setConfirmDelete(true)}
              onCancel={() => setConfirmDelete(false)}
              onConfirm={() => {
                setConfirmDelete(false);
                onDelete();
              }}
            />
          )}
        </div>
      )}
    </DetailSheet>
  );
};

export default TimerSetupSheet;
